import React, { useState, useEffect } from 'react';
import { apiFetch } from '@/lib/apiFetch';
import DashboardHeader from '@/components/DashboardHeader';
import AppFooter from '@/components/AppFooter';
import DataRetentionPanel from '@/components/DataRetentionPanel';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Key, Save, Check, AlertCircle, Shield, Trash2, Zap } from 'lucide-react';

export default function SettingsPage() {
  const [openaiKey, setOpenaiKey] = useState('');
  const [keyInfo, setKeyInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok: bool, message: string } | null

  // Auto-clear test result after 10s
  useEffect(() => {
    if (!testResult) return;
    const t = setTimeout(() => setTestResult(null), 10000);
    return () => clearTimeout(t);
  }, [testResult]);

  useEffect(() => {
    fetchKeyInfo();
  }, []);

  const fetchKeyInfo = async () => {
    try {
      const { data } = await apiFetch('/api/settings/openai-key');
      setKeyInfo(data);
    } catch { /* ignore */ }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!openaiKey.trim()) {
      setError('Enter your OpenAI API key');
      return;
    }
    setError('');
    setSaving(true);
    setSaved(false);
    try {
      await apiFetch('/api/settings/openai-key', {
        method: 'POST',
        body: { openai_api_key: openaiKey },
      });
      setSaved(true);
      setOpenaiKey('');
      await fetchKeyInfo();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    setError('');
    try {
      await apiFetch('/api/settings/openai-key', { method: 'DELETE' });
      setShowRemoveConfirm(false);
      setTestResult(null);
      await fetchKeyInfo();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove key');
    } finally {
      setRemoving(false);
    }
  };

  const handleTestKey = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await apiFetch('/api/settings/test-openai-key', { method: 'POST' });
      if (data.success) {
        setTestResult({ ok: true, message: 'API key is valid' });
      } else {
        setTestResult({ ok: false, message: data.error || 'API key test failed' });
      }
    } catch (err) {
      setTestResult({ ok: false, message: err.response?.data?.detail || 'Test request failed' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#141414]" data-testid="settings-page">
      <DashboardHeader />
      <main className="max-w-[700px] mx-auto px-6 py-6 md:py-8">
        <div className="mb-6">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#7B2FFF]">
            Configuration
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#EDEDF0] mt-1" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            Settings
          </h2>
          <p className="text-sm text-[#A1A1AA] mt-1">
            Manage your API keys and account preferences
          </p>
        </div>

        {/* OpenAI API Key Card */}
        <div className="border border-[#27272A] rounded-lg bg-[#1A1A1A] p-6 transition-all duration-200 hover:border-[#7B2FFF]/30">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4 text-[#7B2FFF]" />
              <h3 className="text-sm font-semibold text-[#EDEDF0]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
                OpenAI API Key
              </h3>
            </div>
            {keyInfo?.has_key && (
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs" data-testid="openai-key-badge">
                Configured
              </Badge>
            )}
          </div>

          <div className="flex items-start gap-3 p-3 mb-5 rounded-md bg-[#7B2FFF]/5 border border-[#7B2FFF]/10">
            <Shield className="w-4 h-4 text-[#7B2FFF] mt-0.5 shrink-0" />
            <p className="text-xs text-[#A1A1AA] leading-relaxed">
              Your key is encrypted with AES-256 before storage. It is never displayed in full or sent to the browser.
              Get your key from{' '}
              <a
                href="https://platform.openai.com/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#7B2FFF] hover:text-[#9353FF] underline"
              >
                platform.openai.com
              </a>
            </p>
          </div>

          {/* Vault-style masked key display */}
          {keyInfo?.has_key && (
            <>
              <div className="flex items-center justify-between p-3 mb-3 rounded-md bg-[#111111] border border-[#27272A]">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md bg-[#7B2FFF]/10 flex items-center justify-center">
                    <Key className="w-4 h-4 text-[#7B2FFF]" />
                  </div>
                  <div>
                    <code className="text-sm text-[#EDEDF0] font-mono tracking-wide" data-testid="openai-key-masked">
                      {keyInfo.masked_key}
                    </code>
                    <p className="text-xs text-[#71717A] mt-0.5">Encrypted in vault</p>
                  </div>
                </div>
                {!showRemoveConfirm ? (
                  <div className="flex items-center gap-1.5">
                    <Button
                      data-testid="test-openai-key-button"
                      onClick={handleTestKey}
                      disabled={testing}
                      variant="ghost"
                      size="sm"
                      className="text-[#A1A1AA] hover:text-[#7B2FFF] hover:bg-[#7B2FFF]/5 h-8 px-3 text-xs"
                    >
                      {testing ? (
                        <div className="w-3.5 h-3.5 border-2 border-[#7B2FFF] border-t-transparent rounded-full animate-spin mr-1.5" />
                      ) : (
                        <Zap className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Test API Key
                    </Button>
                    <Button
                      data-testid="remove-key-button"
                      onClick={() => setShowRemoveConfirm(true)}
                      variant="ghost"
                      size="sm"
                      className="text-[#71717A] hover:text-red-400 hover:bg-red-400/5 h-8 px-3"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove Key
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid="confirm-remove-key"
                      onClick={handleRemove}
                      disabled={removing}
                      size="sm"
                      className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 h-8 px-3 text-xs"
                    >
                      {removing ? (
                        <div className="w-3 h-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                      ) : 'Yes, remove'}
                    </Button>
                    <Button
                      data-testid="cancel-remove-key"
                      onClick={() => setShowRemoveConfirm(false)}
                      variant="ghost"
                      size="sm"
                      className="text-[#71717A] hover:text-[#EDEDF0] h-8 px-3 text-xs"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>

              {/* Inline test result */}
              {testResult && (
                <div
                  data-testid="test-key-result"
                  className={`flex items-center gap-2 p-2.5 mb-4 rounded-md text-xs border ${
                    testResult.ok
                      ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-400'
                      : 'bg-red-500/5 border-red-500/20 text-red-400'
                  }`}
                >
                  {testResult.ok ? (
                    <Check className="w-3.5 h-3.5 shrink-0" data-testid="test-key-result-success" />
                  ) : (
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" data-testid="test-key-result-error" />
                  )}
                  <span className="leading-relaxed">{testResult.message}</span>
                </div>
              )}
            </>
          )}

          {error && (
            <div className="flex items-center gap-2 p-2.5 mb-4 border border-red-500/20 rounded-md bg-red-500/5 text-red-400 text-xs">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#A1A1AA]">
                {keyInfo?.has_key ? 'Replace API Key' : 'API Key'}
              </Label>
              {keyInfo?.has_key && (
                <p className="text-xs text-[#71717A] mb-1">
                  Key saved — enter a new key to replace it.
                </p>
              )}
              <Input
                data-testid="openai-key-input"
                type="password"
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
                placeholder="sk-..."
                className="bg-[#111111] border-[#27272A] text-[#EDEDF0] placeholder:text-[#71717A] focus:border-[#7B2FFF] font-mono text-sm rounded-md"
              />
            </div>

            <Button
              data-testid="openai-key-save-button"
              type="submit"
              disabled={saving || !openaiKey.trim()}
              className="bg-[#7B2FFF] hover:bg-[#9353FF] text-white font-medium rounded-md h-10 text-sm transition-all duration-200 hover:shadow-[0_0_16px_rgba(123,47,255,0.25)] active:scale-[0.98] disabled:opacity-40"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : saved ? (
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4" /> Key Saved</span>
              ) : (
                <span className="flex items-center gap-1.5"><Save className="w-4 h-4" /> Save API Key</span>
              )}
            </Button>
          </form>
        </div>

        {/* Data Retention Panel */}
        <div className="mt-6">
          <DataRetentionPanel />
        </div>
        <AppFooter />
      </main>
    </div>
  );
}
